import { useAppSelector } from '@common/hooks'
import cx from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import type { SwiperClass, SwiperRef } from 'swiper/react'
import { Swiper, SwiperSlide } from 'swiper/react'

import classNames from './styles.module.scss'
import CarouselNavigation from './widgets/CarouselNavigation'
import ModalScreenshot from './widgets/ModalScreenshot'


interface IProps {
  screenshots: string[];
}

export default function Screenshots({
  screenshots,
}: IProps): JSX.Element | null {
  const slider = useRef<SwiperRef>(null)
  const validBasePath = useAppSelector(store => store.common.data.config?.IMAGE_DOMAIN)
  const [isStart, setIsStart] = useState(false)
  const [isEnd, setIsEnd] = useState(false)
  const [snapGrid, setSnapGrid] = useState(0)
  const [showNavigation, setShowNavigation] = useState(false)
  const [activeScreenshot, setActiveScreenshot] = useState<number | null>(null)
  const [openModal, setOpenModal] = useState(false)
  const [loadedImages, setLoadedImages] = useState<boolean[]>(new Array(screenshots.length).fill(false))

  const handleImageLoad = (index: number) => {
    setLoadedImages(prev => {
      const newLoadedImages = [...prev]
      newLoadedImages[index] = true
      return newLoadedImages
    })
  }

  const screenShotsRender = useMemo(() => {
    if(!screenshots?.length || !validBasePath) {
      return []
    }
    return screenshots.map((screenshot, ind) => {
      const newUrl = `${validBasePath}${validBasePath.endsWith('/') ? '' : '/'}${screenshot}`
      return (
        <SwiperSlide key={screenshot} className={classNames.slide}>
          {!loadedImages[ind] && (
            <Skeleton className={classNames.skeleton} duration={0.5} containerClassName={classNames.skeleton}/>
          )}
          <img
            src={newUrl}
            className={classNames.img}
            onClick={() => setActiveScreenshot(ind)}
            onLoad={() => handleImageLoad(ind)}
            style={{ display: loadedImages[ind] ? 'block' : 'none' }}
            alt={`Screenshot ${ind + 1}`}
            data-test="screenshot"
          />
        </SwiperSlide>
      )
    })
  }, [screenshots, loadedImages])

  const onSwipperCallback = (swiper: SwiperClass): void => {
    setSnapGrid(swiper.snapGrid.length)
    if(swiper.isEnd) {
      setIsEnd(true)
      setIsStart(false)
      return
    } else if(swiper.isBeginning) {
      setIsEnd(false)
      setIsStart(true)
      return
    }
    setIsEnd(false)
    setIsStart(false)
  }

  useEffect(() => {
    if(activeScreenshot !== null && openModal === false) {
      setOpenModal(true)
    }
  }, [activeScreenshot])

  if(!screenShotsRender.length) {
    return null
  }

  return (
    <div
      className={classNames.wrapper}
      onMouseEnter={() => setShowNavigation(true)}
      onMouseLeave={() => setShowNavigation(false)}
      data-test="screenshots"
    >
      <ModalScreenshot
        slider={slider.current?.swiper}
        show={openModal}
        onHide={() => {
          setOpenModal(false)
          setTimeout(() => setActiveScreenshot(null), 500)
        }}
        activeElem={activeScreenshot}
        screenshots={screenshots}
        setActiveScreenshot={setActiveScreenshot}
      />
      {snapGrid > 1 && (
        <CarouselNavigation
          slider={slider.current?.swiper}
          isEnd={isEnd}
          isStart={isStart}
          show={showNavigation}
        />
      )}
      <Swiper
        slidesPerView={'auto'}
        spaceBetween={30}
        pagination={{
          clickable: true,
        }}
        className={cx(classNames.swipper, {
          [classNames.showTransition]: !isEnd,
        })}
        ref={slider}
        onSwiper={onSwipperCallback}
        onSlideChange={onSwipperCallback}
        onResize={onSwipperCallback}
        onSlidesGridLengthChange={onSwipperCallback}
      >
        {screenShotsRender}
      </Swiper>
    </div>
  )
}
